import Swal from "sweetalert2";
import React, { useRef } from "react";
import {
    intl, DmsButtonGroup, DmsPermissions, DmsTable,
    DmsTableCreateButton, DmsTableDeleteButton, DmsTableEditButton,
    DmsFlexContainer, fetchData, Permissions, HTTP_STATUS
} from "dmsbasereact";

import { JSONViewerButton } from "./JSONViewerButton";
import { SAPRequestStatus, SAPRequestType } from "../../../constants/SAPCommunicationEnum";

/**
 * SAPCommunicationTable Component
 * @component
 *
 * @description
 * Componente de tabela para gerenciar comunicações com o SAP.
 * Mostra tipo de requisição, payload, status, tentativas e resposta.
 * Permite criar, editar e deletar registros de comunicação SAP.
 *
 * @returns {JSX.Element}
 */
export const SAPCommunicationTable = () => {
    const table = useRef(null);

    const header = [
        {
            label: intl.get("UpdatedOn").d("Updated On"),
            column_name: "updated_on",
            filter: false
        },
        {
            label: intl.get("RequestType").d("Request Type"),
            column_name: "request_type",
            map: SAPRequestType,
            control: 'select',
            filter: true
        },
        {
            label: intl.get("RequestStatus").d("Request Status"),
            column_name: "request_status",
            map: SAPRequestStatus,
            control: 'select',
            filter: true
        },
        {
            label: intl.get("Attempts").d("Attempts"),
            column_name: "attempts",
            filter: true
        },
        {
            label: intl.get("Payload").d("Payload"),
            column_name: "payload",
            filter: false,
            sortable: false,
            element: (row) => (
                <JSONViewerButton
                    label={intl.get("ViewPayload").d("View Payload")}
                    data={row.payload}
                />
            )
        },
        {
            label: intl.get("Response").d("Response"),
            column_name: "response",
            filter: false,
            sortable: false,
            element: (row) => (
                <JSONViewerButton
                    label={intl.get("ViewResponse").d("View Response")}
                    data={row.response}
                />
            )
        },
        {
            label: intl.get("Actions").d("Actions"),
            column_name: "actions",
            permissions: [Permissions.ADMIN_PERMISSION],
            width: "80px",
            sortable: false,
            element: (row) => (
                <DmsButtonGroup>
                    <DmsTableEditButton
                        table={table}
                        title={intl.get("UpdateSAPCommunication").d("Update SAP Communication")}
                        url="/sap-communication/"
                        row={row}
                    />
                    <DmsTableDeleteButton
                        table={table}
                        title={intl.get("DeleteSAPCommunication").d("Delete SAP Communication")}
                        text={intl.get("TheSAPCommunicationWillBeDeleted").d("The SAP communication will be deleted. Are you sure?")}
                        url="/sap-communication/"
                        row_pk={row.pk}
                    />
                </DmsButtonGroup>
            )
        },
    ];

    const createSAPCommunication = async (data) => {
        const url = "/sap-communication";
        const response = await fetchData(url, "POST", true, data);

        if (response.status === HTTP_STATUS.CREATED_201) {
            Swal.fire({
                icon: "success",
                title: intl.get("SAPCommunicationCreated").d("SAP communication created"),
            });

            table.current.updateTable();
        } else {
            Swal.fire({
                icon: "error",
                title: response?.data?.detail || intl.get("Error").d("Error"),
            });
        }
    };

    const toolbar = (
        <DmsFlexContainer>
            <DmsFlexContainer.Left>
                <DmsPermissions permissions={Permissions.ADMIN_PERMISSION}>
                    <DmsTableCreateButton
                        header={header}
                        table={table}
                        title={intl.get("CreateSAPCommunication").d("Create SAP Communication")}
                        handler={createSAPCommunication}
                    />
                </DmsPermissions>
            </DmsFlexContainer.Left>
        </DmsFlexContainer>
    );

    return (
        <DmsTable
            header={header}
            managed={true}
            needs_authentication={true}
            export_table={false}
            ref={table}
            url={'/sap-communication/table'}
            toolbar={toolbar}
        />
    );
};
